import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useToast } from '../../components/useToast';
import { type GridColDef } from '@mui/x-data-grid';
import { useServerListParams, useListDeleteHandler } from '@crm/shared';
import { ServerDataGrid } from '../../components/ServerDataGrid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchQueues, createQueue, updateQueue, deleteQueue } from './queueThunks';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { EditableField } from '../../components/EditableField';

export default function QueueList() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const toast = useToast();
  const { items, status, error, totalCount } = useAppSelector((state) => state.queues);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const {
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    resetPage,
    backendParams,
  } = useServerListParams();

  useEffect(() => {
    dispatch(fetchQueues(backendParams));
  }, [backendParams, dispatch]);

  // Delete + refetch coerente con totalCount/paginazione (vedi TS-9).
  const handleDelete = useListDeleteHandler({
    deleteThunk: deleteQueue,
    fetchThunk: fetchQueues,
    fetchParams: backendParams,
    itemsLength: items.length,
    page: paginationModel.page,
    resetPage,
    dispatch,
  });

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    const description = newDescription.trim();
    try {
      await dispatch(createQueue({ name: newName.trim(), description: description || null })).unwrap();
      setNewName('');
      setNewDescription('');
      // Refresh dopo create: il createQueue.fulfilled aggiorna items
      // ma non totalCount, quindi rifacciamo la fetch corrente.
      dispatch(fetchQueues(backendParams));
      toast({ severity: 'success', message: t('common.saved') });
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  if (status === 'failed') return <p className="text-error">{t('common.error', { message: error })}</p>;

  return (
    <div className={"p-md"}>
      <h2>{t('queue.title')}</h2>

      <form onSubmit={handleCreate} className="flex-col gap-sm mb-xl">
        <TextField value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('queue.placeholder')} required
          size="small" fullWidth />
        <TextField value={newDescription} onChange={(e) => setNewDescription(e.target.value)}
          placeholder={t('queue.descriptionPlaceholder')} size="small" fullWidth multiline minRows={2} />
        <Button type="submit" variant="contained" className="self-start">
          {t('common.add')}
        </Button>
      </form>

      {status === 'succeeded' && items.length === 0 ? <p className="text-muted">{t('queue.noQueues')}</p> : (
        <ServerDataGrid
          rows={items}
          columns={queueColumns(confirm, dispatch, t, toast, handleDelete)}
          getRowId={(row) => row.queueId}
          getRowHeight={() => 'auto'}
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          loading={status === 'loading'}
          className="datagrid-standard"
        />
      )}
      <ConfirmDialog open={confirm.isOpen} message={confirm.message} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
    </div>
  );
}

function queueColumns(
  confirm: ReturnType<typeof useConfirmDialog>,
  dispatch: ReturnType<typeof useAppDispatch>,
  t: (key: string, opts?: Record<string, unknown>) => string,
  toast: ReturnType<typeof useToast>,
  handleDelete: (queueId: number) => void,
): GridColDef[] {
  return [
    {
      field: 'name', headerName: t('queue.columns.name'), flex: 1,
      // Editing inline del name: doppio click apre l'edit, blur/Enter
      // committa via updateQueue, Esc cancella. Toast success/error
      // gestito dentro onCommit. Il throw rilancia all'EditableField
      // così tiene aperto l'edit con messaggio inline su fallimento.
      renderCell: (params) => (
        <EditableField
          value={params.row.name}
          variant={{ kind: 'text' }}
          required
          flat
          onCommit={async (newValue) => {
            const trimmed = String(newValue ?? '').trim();
            if (!trimmed || trimmed === params.row.name) return;
            try {
              await dispatch(updateQueue({ queueId: params.row.queueId, name: trimmed })).unwrap();
              toast({ severity: 'success', message: t('common.saved') });
            } catch (err) {
              toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
              throw err;
            }
          }}
        />
      ),
    },
    {
      field: 'description', headerName: t('queue.columns.description'), flex: 2, sortable: false,
      // Inline edit textarea: la description e' opzionale, quindi niente `required`.
      // Commit invia anche il name corrente (il backend richiede name not-null in
      // validate). trimmed vuoto -> null (pulisce la description).
      renderCell: (params) => (
        <EditableField
          value={params.row.description ?? ''}
          variant={{ kind: 'textarea', rows: 2 }}
          flat
          emptyLabel={t('queue.noDescription')}
          onCommit={async (newValue) => {
            const trimmed = String(newValue ?? '').trim();
            if (trimmed === (params.row.description ?? '')) return;
            try {
              // trimmed '' (svuotato) -> il backend azzera via GetterUtil.getString("", ...);
              // NON null, che GetterUtil interpreterebbe come "mantieni il valore corrente".
              await dispatch(updateQueue({
                queueId: params.row.queueId, name: params.row.name, description: trimmed,
              })).unwrap();
              toast({ severity: 'success', message: t('common.saved') });
            } catch (err) {
              toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
              throw err;
            }
          }}
        />
      ),
    },
    {
      field: 'actions', headerName: t('queue.columns.actions'), sortable: false, width: 120,
      renderCell: (params) => (
        <Button
          onClick={() => confirm.requestConfirm(t('queue.deleteConfirm', { name: params.row.name }), () => handleDelete(params.row.queueId))}
          variant="text" size="small" color="error">
          {t('common.delete')}
        </Button>
      ),
    },
  ];
}
